import type { Progress, SessionRecord } from '@/domain/session'
import { emptyProgress } from '@/domain/session'

/**
 * Puerta de salida del progreso. Hoy la implementa el almacenamiento del
 * navegador; el día que haya cuentas, una implementación contra Supabase
 * entra aquí sin tocar la interfaz ni los componentes.
 */
export interface ProgressRepository {
  load(challengeId: string): Promise<Progress>
  save(progress: Progress): Promise<void>
}

/** Formato en disco. La versión permite migrar sin perder sesiones. */
interface StoredProgress {
  version: 1
  challengeId: string
  sessions: SessionRecord[]
}

const STORAGE_PREFIX = 'tfc:progreso:'
const CURRENT_VERSION = 1 as const

const keyFor = (challengeId: string) => `${STORAGE_PREFIX}${challengeId}`

function parse(raw: string, challengeId: string): Progress {
  const parsed: unknown = JSON.parse(raw)
  if (!parsed || typeof parsed !== 'object') return emptyProgress(challengeId)

  const stored = parsed as Partial<StoredProgress>
  if (stored.version !== CURRENT_VERSION || !Array.isArray(stored.sessions)) {
    return emptyProgress(challengeId)
  }

  return { challengeId, sessions: stored.sessions }
}

/**
 * El almacenamiento del navegador es síncrono, pero la interfaz es asíncrona
 * a propósito: así una futura implementación en red encaja sin cambiar nada
 * de quien la consume.
 */
export class LocalProgressRepository implements ProgressRepository {
  load(challengeId: string): Promise<Progress> {
    try {
      const raw = localStorage.getItem(keyFor(challengeId))
      return Promise.resolve(raw ? parse(raw, challengeId) : emptyProgress(challengeId))
    } catch {
      // Modo privado de Safari o almacenamiento lleno: se sigue pudiendo entrenar.
      return Promise.resolve(emptyProgress(challengeId))
    }
  }

  save(progress: Progress): Promise<void> {
    const payload: StoredProgress = {
      version: CURRENT_VERSION,
      challengeId: progress.challengeId,
      sessions: [...progress.sessions],
    }
    try {
      localStorage.setItem(keyFor(progress.challengeId), JSON.stringify(payload))
    } catch {
      // Sin persistencia el entreno funciona igual: solo no se recuerda.
    }
    return Promise.resolve()
  }
}

/** Texto que el alumno puede copiarse como copia de seguridad manual. */
export function exportProgress(progress: Progress): string {
  const payload: StoredProgress = {
    version: CURRENT_VERSION,
    challengeId: progress.challengeId,
    sessions: [...progress.sessions],
  }
  return JSON.stringify(payload)
}

/** Lee una copia de seguridad. Devuelve null si el texto no es válido. */
export function importProgress(raw: string): Progress | null {
  try {
    const parsed: unknown = JSON.parse(raw.trim())
    if (!parsed || typeof parsed !== 'object') return null
    const stored = parsed as Partial<StoredProgress>
    if (stored.version !== CURRENT_VERSION || !stored.challengeId || !Array.isArray(stored.sessions)) {
      return null
    }
    return { challengeId: stored.challengeId, sessions: stored.sessions }
  } catch {
    return null
  }
}

export const progressRepository: ProgressRepository = new LocalProgressRepository()
